/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { DispatchDragNDropTypes, DispatchOperationType, DispatchStatus } from '~/utils';
import { useDispatch } from 'react-redux';
import styles from '../styles';
import PF2MEquipIcon from '~/components/PF2MEquipIcon';
import PF2MDispatchIcon from '~/components/PF2MDispatchIcon';
import { setSelectedElementDestination, setSelectedTruckDestination } from '~/store/dispatch/actions';

const getWarningType = (truckOrigin, destinations, isGenericRow) => {
  if (isGenericRow) return 'standardWarning';
  const expectedDestination = destinations
    .find(r => r.id === truckOrigin.destination_point_id);

  if (expectedDestination
    && truckOrigin.current_destination_id !== expectedDestination.id) {
    return 'standardWarning';
  }
  return null;
};

const getWarningTypeForDestination = (destinationRow) => {
  if (!destinationRow.is_programmed) return 'standardWarning';
  return '';
};

const DestinationRow = ({
  onTruckDrop,
  destination,
  leftLane,
  rightLane,
  isGenericRow = false,
  destinations,
  openTruckModal,
  openElementModal,
  onTruckContextMenu,
}) => {
  const dispatch = useDispatch();

  const order = [
    DispatchOperationType.LOAD,
    DispatchOperationType.UNLOAD,
    DispatchOperationType.LOAD_MANEUVER,
    DispatchOperationType.UNLOAD_MANEUVER,
    DispatchOperationType.LOAD_QUEUE,
    DispatchOperationType.UNLOAD_QUEUE,
    DispatchOperationType.LOAD_TRAFFIC,
    DispatchOperationType.UNLOAD_TRAFFIC,
  ];

  const leftLaneDurations = useMemo(() => leftLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [leftLane, order]);


  const rightLaneDurations = useMemo(() => rightLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [rightLane, order]);

  const [{ dragItem }, truckDrop] = useDrop({
    accept: DispatchDragNDropTypes.DESTINATION_TRUCK,
    drop: (monitor) => {
      if (destination.id !== monitor.obj.destination_point_id && destination.id !== null
        && monitor.obj.current_destination_id !== destination.id && destination.is_programmed) {
        onTruckDrop(monitor.obj, destination.id);
      }
    },
    collect: monitor => ({
      dragItem: monitor.getItem(),
    }),
  });

  const dispatchIconStyle = {
    marginLeft: 15,
    marginRight: 15,
    cursor: 'pointer',
  };

  return (
    <div
      key={destination.id}
      style={{
        display: 'flex',
        width: '100%',
        height: 'fit-content',
        backgroundColor: '#dedede',
      }}
      ref={truckDrop}
    >
      <div
        style={{
          width: '50%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          border: '1px solid #CDCDCD',
          height: 'auto',
          paddingBlock: 5,
        }}
      >
        <div>
          <PF2MDispatchIcon
            iconType="excavator"
            customStyles={dispatchIconStyle}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            leftLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={r.current_excavator_name}
                item={r}
                itemType={DispatchDragNDropTypes.DESTINATION_TRUCK}
                useOperationType={r.status === DispatchStatus.OP}
                customStyles={{
                  marginRight: 15,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                onClick={() => {
                  dispatch(setSelectedTruckDestination(r));
                  openTruckModal(true);
                }}
                warningType={getWarningType(r, destinations, isGenericRow)}
                onContextMenu={onTruckContextMenu}
                tooltipData={r}
                v2
              />
            ))
          }
        </div>
      </div>
      <div
        style={{
          width: '50%',
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          border: '1px solid #CDCDCD',
          paddingBlock: 5,
        }}
      >
        <div>
          <PF2MDispatchIcon
            iconType="stack"
            subtitle={isGenericRow ? '' : destination.name}
            customStyles={dispatchIconStyle}
            warningType={getWarningTypeForDestination(destination)}
            onClick={() => {
              if (!isGenericRow) {
                dispatch(setSelectedElementDestination(destination));
                openElementModal(true);
              }
            }}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            rightLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={r.current_destination_point_name}
                item={r}
                itemType={DispatchDragNDropTypes.DESTINATION_TRUCK}
                useOperationType={r.status === DispatchStatus.OP}
                customStyles={{
                  marginRight: 15,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                onClick={() => {
                  dispatch(setSelectedTruckDestination(r));
                  openTruckModal(true);
                }}
                warningType={getWarningType(r, destinations, isGenericRow)}
                onContextMenu={onTruckContextMenu}
                tooltipData={r}
                v2
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};

DestinationRow.propTypes = {
  onTruckDrop: PropTypes.func,
  leftLane: PropTypes.array,
  rightLane: PropTypes.array,
  isGenericRow: PropTypes.bool,
  destination: PropTypes.object,
  destinations: PropTypes.array,
  openTruckModal: PropTypes.func,
  openElementModal: PropTypes.func,
  onTruckContextMenu: PropTypes.func,
};

DestinationRow.defaultProps = {
  onTruckDrop: () => null,
  leftLane: [],
  rightLane: [],
  isGenericRow: false,
  destination: {},
  destinations: [],
  openTruckModal: () => null,
  openElementModal: () => null,
  onTruckContextMenu: null,
};

export default withStyles(styles)(DestinationRow);
